// app/features/staff_access/services_and_hooks/useStaffAccessHooks.ts
"use client";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import type {
  InviteStaffAccessInput,
  UpdateStaffAccessInput,
} from "../staff_access.interfaces";
import {
  disableStaffAccessUser,
  fetchMyStaffAccess,
  inviteStaffAccessUser,
  listStaffAccessUsers,
  updateStaffAccessUser,
} from "./staffAccessService";

export const staffAccessKeys = {
  all: ["staff-access"] as const,
  me: () => [...staffAccessKeys.all, "me"] as const,
  list: () => [...staffAccessKeys.all, "list"] as const,
};

export function useMyStaffAccessHook(enabled = true) {
  return useQuery({
    queryKey: staffAccessKeys.me(),
    queryFn: fetchMyStaffAccess,
    enabled,
    staleTime: 30_000,
    retry: false,
  });
}

export function useStaffAccessUsersHook(enabled = true) {
  return useQuery({
    queryKey: staffAccessKeys.list(),
    queryFn: listStaffAccessUsers,
    enabled,
    staleTime: 15_000,
  });
}

export function useInviteStaffAccessHook() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: InviteStaffAccessInput) => inviteStaffAccessUser(input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: staffAccessKeys.list() });
    },
  });
}

export function useUpdateStaffAccessHook() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateStaffAccessInput) => updateStaffAccessUser(input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: staffAccessKeys.list() });
      void qc.invalidateQueries({ queryKey: staffAccessKeys.me() });
    },
  });
}

export function useDisableStaffAccessHook() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => disableStaffAccessUser(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: staffAccessKeys.list() });
    },
  });
}
